/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Row } from "@tanstack/react-table";
import {
    Button,
    Checkbox,
    ColumnDef,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Pagination,
    Table,
} from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    IClaim,
    ClaimStatus,
    ClaimsHasRemit,
    ClaimActivityType,
} from "../../../types/claims.types";
import {
    cn,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../../utils/helpers";
import {
    getClaimActivityToDisplay,
    getStatusTag,
} from "../../../helpers/claims.helper";
import ListState from "../../../../../components/ListState/ListState";
import Skeleton from "../../../../../components/Skeleton/Skeleton";
import { useFetchClaims } from "../../../hooks/claims.queries";
import { IAppCustomDates } from "../../../../../utils/types";
import MoreButton from "../../../../../shared-ui/Buttons/MoreButton/MoreButton";
import {
    useChangeClaimVoidStatus,
    useUpdateClaimsActivity,
    useUpdateClaimStatus,
} from "../../../hooks/claims.mutations";
import useToast from "../../../../../hooks/useToast";
import { LISTS_DEFAULT_LIMIT } from "../../../../../utils/constants";
import BulkUpdateClaimForm from "../BulkUpdateClaimsForm/BulkUpdateClaimsForm";
import FilterIcon from "../../../../../components/Icons/Filter";
import { Source } from "../../../../../utils/types/notes";
import TableFiltersDisplay from "../../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { useFetchUserDetails } from "../../../../../hooks/queries/user";

interface ClosedClaimsListProps {
    searchString: string;
}

type Dialogs = "bulk_update_status";

export default function ClosedClaimsList({
    searchString,
}: ClosedClaimsListProps) {
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedClaimsData, setGetSelectedClaimsData] = React.useState<
        Row<IClaim>[]
    >([]);
    const [statusToSubmit, setStatusToSubmit] =
        React.useState<ClaimStatus | null>(null);

    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const fromDateFilter = searchParams.get("note_dos_from");
    const toDateFilter = searchParams.get("note_dos_to");
    const insuranceProviderFilter =
        searchParams.get("insurance_provider") || "";
    const customDateFilter =
        (searchParams.get("date_range") as IAppCustomDates) ||
        IAppCustomDates.LAST_90_DAYS;
    const claimsStatusFilter = searchParams.get("status") as ClaimStatus;
    const hasRemitStatusFilter = searchParams.get("has_remits") || "";
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });
    const showVoidedFilter = searchParams.get("show_voided") || "false";

    // Column filters
    const clientColFilter = searchParams.get("client_filter");
    const clinicianColFilter = searchParams.get("clinician_filter");
    const dosColFilter = searchParams.get("date_of_service_filter");
    const statusColFilter = searchParams.get("status_filter");
    const sourceColFilter = searchParams.get("source_filter");
    const insuraceColFilter = searchParams.get("insurance_filter");
    const cptColFilter = searchParams.get("cpt_filter");
    const acceptAssignFilter = searchParams.get("accepts_assignment_filter");
    const attachDocFilter = searchParams.get("attached_doc_filter");
    const claimsWithRemitsFilter = searchParams.get("has_remits_filter");

    const sortableColumns = [
        "generated_on",
        "date_of_service",
        "submitted_on",
        "insurance_name",
        "claim_status",
        "client_name",
        "cpt_code",
        "note_author",
    ];

    const loggedInUser = useFetchUserDetails();

    const patchClaimStatus = useUpdateClaimStatus();

    const queryClient = useQueryClient();

    const isMutating = useIsMutating(["bulk-update-claims"]);

    // hook for voiding/unvoiding claim
    const changeVoidStatus = useChangeClaimVoidStatus();
    const updateClaimsActivity = useUpdateClaimsActivity();

    const { toast } = useToast();

    const getStatusFilter = () => {
        if (claimsStatusFilter) {
            if ([ClaimStatus.CLOSED].includes(claimsStatusFilter)) {
                return [claimsStatusFilter];
            }

            return [""];
        }

        return [ClaimStatus.CLOSED];
    };
    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const getHasRemitFilter = () => {
        if (hasRemitStatusFilter === ClaimsHasRemit.SHOW_CLAIMS_WITH_REMIT) {
            return "true";
        }
        if (hasRemitStatusFilter === ClaimsHasRemit.HIDE_CLAIMS_WITH_REMIT) {
            return "false";
        }
        return "";
    };

    const claimSourceFilter = searchParams.get("source") || "";

    const { data, error, isLoading, isSuccess } = useFetchClaims({
        claim_statuses: getStatusFilter(),
        custom_date: customDateFilter,
        search_string: searchString,
        note_dos_from: fromDateFilter || undefined,
        note_dos_to: toDateFilter || undefined,
        insurance_provider_id: insuranceProviderFilter,
        page: pageFilter,
        limit: limitFilter,
        has_remits: getHasRemitFilter(),
        sort_attribute:
            searchParams.get("closed_claims_sort_attr") ||
            handleConfigAttribute(),
        sort_order:
            searchParams.get("closed_claims_sort_order") ||
            sortConfig.direction,
        claim_source: claimSourceFilter,
        show_voided: showVoidedFilter,
    });

    const selectedClaimsIds =
        getSelectedClaimsData &&
        getSelectedClaimsData.map(
            (item: Row<IClaim>) => item.original.claim_id
        );

    const claimsDetails =
        selectedClaimsIds &&
        selectedClaimsIds.map((id: string) =>
            data?.data.find((claim) => claim.claim_id === id)
        );

    const showUnvoidClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => claim && claim.is_voided).length;

    const showVoidClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => claim && !claim.is_voided).length;

    const showUnassignClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    claim?.claim_activities || [],
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return isClaimAssignedToMe;
            }).length;

    const showAssignClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    claim?.claim_activities || [],
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return !isClaimAssignedToMe;
            }).length;

    const handleChangeStatus = (id: string, status: ClaimStatus) => {
        const changeStatusPayload = {
            claim_id: id,
            claim_status: status,
        };
        patchClaimStatus.mutate(changeStatusPayload, {
            onSuccess: async (response) => {
                await queryClient.invalidateQueries(["claims"]);
                toast({
                    mode: "success",
                    message:
                        response?.message ||
                        "Claim status updated successfully",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update claim status at this time",
                });
            },
        });
    };

    const handleChangeVoidStatus = (status: boolean, id?: string) => {
        const payload = {
            claim_ids: id ? [id] : selectedClaimsIds,
            void_status: status,
        };

        changeVoidStatus.mutate(payload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries(["claims"]);
                toast({
                    mode: "success",
                    message:
                        response?.message ||
                        `Claim ${
                            status === true ? "voided" : "unvoided"
                        } successfully`,
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        `Could not ${
                            status === true ? "void" : "unvoid"
                        } claim at this time`,
                });
            },
        });
    };

    const handleDialogInView = (dialog: Dialogs | null) => {
        setDialogInView(dialog);
    };

    const handleDisplayBulkUpdateStatusDialog = (status: ClaimStatus) => {
        setStatusToSubmit(status);
        handleDialogInView("bulk_update_status");
    };

    const handleCloseBulkUpdateStatusDialog = () => {
        setStatusToSubmit(null);
        setRowSelection({});
        handleDialogInView(null);
    };

    const handleRowClick = (row: Row<IClaim>) => {
        navigate(
            `/claims/${row.original.claim_id}/${row.original.client_id}/${row.original.note_id}?key=claims&claims_view=view&viewFrom=claims&tab=closed&claims_sub_tab=claims&active_claim_id=${row.original.claim_id}`
        );
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("page", String(limit));
        setSearchParams(searchParams);
    };

    const handleUpdateClaimsActivity = (
        activityType: ClaimActivityType,
        claimId?: string
    ) => {
        const dataToSend = {
            claim_ids: claimId ? [claimId] : selectedClaimsIds,
            activity_type: activityType,
            notes: undefined,
        };
        updateClaimsActivity.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries(["claims"]);

                toast({
                    mode: "success",
                    message:
                        res.message || "Claims activity updated successfully!",
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not perform action at this time.",
                });
            },
        });
    };

    const datesOfServiceInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.date_of_service)
                    .filter(Boolean)
            ),
        ].sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    }, [data?.data]);

    const clientsInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.client_name)
                    .filter(Boolean)
            ),
        ];
    }, [data?.data]);

    const noteAuthorsInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.filter((claim: IClaim) => claim.note_author)
                    .map(
                        (claim: IClaim) =>
                            `${claim.note_author?.first_name} ${claim.note_author?.last_name}`
                    )
            ),
        ];
    }, [data?.data]);

    const statusesInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.claim_status)
                    .filter(Boolean)
            ),
        ];
    }, [data?.data]);

    const sourcesInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.claim_source)
                    .filter(Boolean)
            ),
        ];
    }, [data?.data]);

    const insuranceProvidersInClaims = React.useMemo(() => {
        return (
            data?.data?.reduce(
                (acc: { id: string; name: string }[], claim: IClaim) => {
                    if (
                        !acc.some(
                            (provider) =>
                                provider.id === claim.insurance_provider_id
                        )
                    ) {
                        acc.push({
                            id: claim.insurance_provider_id,
                            name: claim.insurance_name,
                        });
                    }
                    return acc;
                },
                []
            ) || []
        );
    }, [data?.data]);

    const cptCodesInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.cpt_code)
                    .filter(Boolean)
            ),
        ].sort();
    }, [data?.data]);

    const assignmentInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map((claim: IClaim) =>
                    claim.accepts_assignment ? "Yes" : "No"
                )
            ),
        ];
    }, [data?.data]);

    const claimsWithRemits = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map((claim: IClaim) =>
                    claim.has_remits ? "Yes" : "No"
                )
            ),
        ];
    }, [data?.data]);

    const attachDocInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map((claim: IClaim) =>
                    claim.has_claim_attachments ? "Yes" : "No"
                )
            ),
        ];
    }, [data?.data]);

    const appliedFilters = React.useMemo(() => {
        const filters = new Set<{ key: string; values: string[] }>();

        if (clientColFilter) {
            filters.add({
                key: "client_filter",
                values: [clientColFilter],
            });
        }
        if (clinicianColFilter) {
            filters.add({
                key: "clinician_filter",
                values: [clinicianColFilter],
            });
        }
        if (dosColFilter) {
            filters.add({
                key: "date_of_service_filter",
                values: [dosColFilter],
            });
        }
        if (insuraceColFilter) {
            filters.add({
                key: "insurance_filter",
                values: [insuraceColFilter],
            });
        }
        if (statusColFilter) {
            filters.add({
                key: "status_filter",
                values: [statusColFilter],
            });
        }
        if (sourceColFilter) {
            filters.add({
                key: "source_filter",
                values: [sourceColFilter],
            });
        }
        if (cptColFilter) {
            filters.add({
                key: "cpt_filter",
                values: [cptColFilter],
            });
        }
        if (claimsWithRemitsFilter) {
            filters.add({
                key: "has_remits_filter",
                values: [claimsWithRemitsFilter === "true" ? "Yes" : "No"],
            });
        }
        if (acceptAssignFilter) {
            filters.add({
                key: "accepts_assignment_filter",
                values: [acceptAssignFilter],
            });
        }
        if (attachDocFilter) {
            filters.add({
                key: "attached_doc_filter",
                values: [attachDocFilter === "true" ? "Yes" : "No"],
            });
        }

        return Array.from(filters);
    }, [
        clientColFilter,
        clinicianColFilter,
        dosColFilter,
        insuraceColFilter,
        statusColFilter,
        sourceColFilter,
        cptColFilter,
        claimsWithRemitsFilter,
        acceptAssignFilter,
        attachDocFilter,
    ]);

    const filteredData = React.useMemo(() => {
        if (!data?.data || !isSuccess) return [];

        let filtered = data.data;

        if (clientColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.client_name === clientColFilter
            );
        }

        if (clinicianColFilter) {
            filtered = filtered.filter((claim: IClaim) => {
                if (!claim.note_author) return false;
                const fullName = `${claim.note_author.first_name} ${claim.note_author.last_name}`;
                return fullName === clinicianColFilter;
            });
        }

        if (dosColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) =>
                    formatDate(claim.date_of_service) === dosColFilter
            );
        }

        if (insuraceColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.insurance_name === insuraceColFilter
            );
        }

        if (cptColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.cpt_code === cptColFilter
            );
        }

        if (statusColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.claim_status === statusColFilter
            );
        }

        if (sourceColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.claim_source === sourceColFilter
            );
        }

        if (acceptAssignFilter === "true") {
            filtered = filtered.filter(
                (claim: IClaim) => claim.accepts_assignment
            );
        }
        if (acceptAssignFilter === "false") {
            filtered = filtered.filter(
                (claim: IClaim) => !claim.accepts_assignment
            );
        }

        if (attachDocFilter === "true") {
            filtered = filtered.filter(
                (claim: IClaim) => claim.has_claim_attachments
            );
        }
        if (attachDocFilter === "false") {
            filtered = filtered.filter(
                (claim: IClaim) => !claim.has_claim_attachments
            );
        }

        if (claimsWithRemitsFilter === "true") {
            filtered = filtered.filter((claim: IClaim) => claim.has_remits);
        }
        if (claimsWithRemitsFilter === "false") {
            filtered = filtered.filter((claim: IClaim) => !claim.has_remits);
        }

        return filtered;
    }, [
        acceptAssignFilter,
        attachDocFilter,
        claimsWithRemitsFilter,
        clientColFilter,
        clinicianColFilter,
        cptColFilter,
        data?.data,
        dosColFilter,
        insuraceColFilter,
        isSuccess,
        sourceColFilter,
        statusColFilter,
    ]);

    const handleColumnFilter = (columnName: string, value?: string) => {
        const paramName = `${columnName}_filter`;

        if (!value) {
            searchParams.delete(paramName);
        } else {
            searchParams.set(
                paramName,
                paramName === "date_of_service_filter"
                    ? formatDate(value)
                    : value
            );
        }
        setSearchParams(searchParams);
    };

    const columns: ColumnDef<IClaim>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "client_name",
                    header: "CLIENT NAME",
                    cell: ({ row }) =>
                        displayNameInRightFormat(row.original.client_name),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={() =>
                                            handleColumnFilter("client")
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary": !clientColFilter,
                                        })}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {clientsInClaims &&
                                        clientsInClaims.map((client) => (
                                            <DropdownItem
                                                key={client}
                                                onClick={() =>
                                                    handleColumnFilter(
                                                        "client",
                                                        client
                                                    )
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            clientColFilter ===
                                                            client,
                                                    }
                                                )}
                                            >
                                                {displayNameInRightFormat(
                                                    client
                                                )}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "date_of_service",
                    header: "DATE OF SERVICE",
                    cell: ({ row }) => formatDate(row.original.date_of_service),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <DropdownItem
                                    onClick={() =>
                                        handleColumnFilter("date_of_service")
                                    }
                                    className={cn("text-xs font-medium", {
                                        "text-primary": !dosColFilter,
                                    })}
                                    closeMenuOnSelect
                                >
                                    All
                                </DropdownItem>
                                {datesOfServiceInClaims.map((option) => (
                                    <DropdownItem
                                        key={option}
                                        onClick={() =>
                                            handleColumnFilter(
                                                "date_of_service",
                                                option
                                            )
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary":
                                                dosColFilter === option,
                                        })}
                                    >
                                        {formatDate(option)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "claim_status",
                    header: "STATUS",
                    cell: ({ row }) => getStatusTag(row.original.claim_status),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={() =>
                                            handleColumnFilter("status")
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary": !statusColFilter,
                                        })}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {statusesInClaims &&
                                        statusesInClaims.map((status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleColumnFilter(
                                                        "status",
                                                        status
                                                    )
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            statusColFilter ===
                                                            status,
                                                    }
                                                )}
                                            >
                                                {removeEnumUnderscore(status)}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "claim_activities",
                    header: "Assigned to",
                    cell: ({ row }) => {
                        const { usersNameToDisplay } =
                            getClaimActivityToDisplay(
                                row.original.claim_activities,
                                ClaimActivityType.IN_PROGRESS,
                                loggedInUser?.data?.user_id ?? ""
                            );

                        return usersNameToDisplay;
                    },
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "claim_source",
                    header: "CLAIM SOURCE",
                    cell: ({ row }) => row.original.claim_source || "--",
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={() =>
                                            handleColumnFilter("source")
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary": !sourceColFilter,
                                        })}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {sourcesInClaims.length > 0 &&
                                        sourcesInClaims.map((source) => (
                                            <DropdownItem
                                                key={source}
                                                onClick={() =>
                                                    handleColumnFilter(
                                                        "source",
                                                        source as Source
                                                    )
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            sourceColFilter ===
                                                            source,
                                                    }
                                                )}
                                            >
                                                {source}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "insurance_name",
                    header: "INSURANCE",
                    cell: ({ row }) => (
                        <span className="uppercase">
                            {row.original.insurance_name}
                        </span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={() =>
                                            handleColumnFilter("insurance")
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary": !insuraceColFilter,
                                        })}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {insuranceProvidersInClaims &&
                                        insuranceProvidersInClaims.map(
                                            (insurance) => (
                                                <DropdownItem
                                                    key={insurance.id}
                                                    onClick={() =>
                                                        handleColumnFilter(
                                                            "insurance",
                                                            insurance.name
                                                        )
                                                    }
                                                    className={cn(
                                                        "text-xs font-medium",
                                                        {
                                                            "text-primary":
                                                                insurance.name ===
                                                                insuraceColFilter,
                                                        }
                                                    )}
                                                >
                                                    {insurance.name}
                                                </DropdownItem>
                                            )
                                        )}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "note_author",
                    header: "CLINICIAN",
                    cell: ({ row }) =>
                        row.original.note_author
                            ? displayNameInRightFormat({
                                  firstName:
                                      row.original.note_author.first_name,
                                  lastName: row.original.note_author.last_name,
                              })
                            : "--",
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={() =>
                                            handleColumnFilter("clinician")
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary": !clinicianColFilter,
                                        })}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {noteAuthorsInClaims &&
                                        noteAuthorsInClaims.map((author) => (
                                            <DropdownItem
                                                key={author}
                                                onClick={() =>
                                                    handleColumnFilter(
                                                        "clinician",
                                                        author
                                                    )
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            clinicianColFilter ===
                                                            author,
                                                    }
                                                )}
                                            >
                                                {displayNameInRightFormat(
                                                    author
                                                )}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "cpt_code",
                    header: "CPT CODE",
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="normal"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width="300px"
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={() =>
                                            handleColumnFilter("cpt")
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary": !cptColFilter,
                                        })}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {cptCodesInClaims &&
                                        cptCodesInClaims.map((code) => (
                                            <DropdownItem
                                                key={code}
                                                onClick={() =>
                                                    handleColumnFilter(
                                                        "cpt",
                                                        code
                                                    )
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            cptColFilter ===
                                                            code,
                                                    }
                                                )}
                                            >
                                                {code}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "charge_amount",
                    header: "CHARGE",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(row.original.charge_amount),
                },
            ],
        },
        {
            header: "OUTSTANDING",
            columns: [
                {
                    accessorKey: "insurance_amount_outstanding",
                    header: "INSURANCE",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(
                            row.original.insurance_amount_outstanding
                        ),
                },
                {
                    accessorKey: "coinsurance_amount_outstanding",
                    header: "CO-INSURANCE",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(
                            row.original.coinsurance_amount_outstanding
                        ),
                },
            ],
        },
        {
            accessorKey: "has_remits",
            header: "HAS REMITS",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.has_remits ? "text-primary" : "text-danger"
                    }
                >
                    {row.original.has_remits ? "Yes" : "No"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="normal"
                            onClick={(e) => e.stopPropagation()}
                            className="px-4"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent
                        width="300px"
                        maxHeight={216}
                        className="overflow-y-auto"
                    >
                        <>
                            <DropdownItem
                                onClick={() => handleColumnFilter("has_remits")}
                                className={cn("text-xs font-medium", {
                                    "text-primary": !claimsWithRemitsFilter,
                                })}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {claimsWithRemits &&
                                claimsWithRemits.map((item) => (
                                    <DropdownItem
                                        key={item}
                                        onClick={() =>
                                            handleColumnFilter(
                                                "has_remits",
                                                item === "Yes"
                                                    ? "true"
                                                    : "false"
                                            )
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary":
                                                (item === "Yes" &&
                                                    claimsWithRemitsFilter ===
                                                        "true") ||
                                                (item === "No" &&
                                                    claimsWithRemitsFilter ===
                                                        "false"),
                                        })}
                                    >
                                        {item}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "accepts_assignment",
            header: "ACCEPTS ASSIGNMENT",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.accepts_assignment
                            ? "text-primary"
                            : "text-danger"
                    }
                >
                    {row.original.accepts_assignment ? "Yes" : "No"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="normal"
                            onClick={(e) => e.stopPropagation()}
                            className="px-4"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent
                        width="300px"
                        maxHeight={216}
                        className="overflow-y-auto"
                    >
                        <>
                            <DropdownItem
                                onClick={() =>
                                    handleColumnFilter("accepts_assignment")
                                }
                                className={cn("text-xs font-medium", {
                                    "text-primary": !acceptAssignFilter,
                                })}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {assignmentInClaims &&
                                assignmentInClaims.map((item) => (
                                    <DropdownItem
                                        key={item}
                                        onClick={() =>
                                            handleColumnFilter(
                                                "accepts_assignment",
                                                item
                                            )
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary":
                                                acceptAssignFilter === item,
                                        })}
                                    >
                                        {item}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "has_claim_attachments",
            header: "Attached doc",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.has_claim_attachments
                            ? "text-primary"
                            : "text-danger"
                    }
                >
                    {row.original.has_claim_attachments ? "Yes" : "No"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="normal"
                            onClick={(e) => e.stopPropagation()}
                            className="px-4"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent
                        width="300px"
                        maxHeight={216}
                        className="overflow-y-auto"
                    >
                        <>
                            <DropdownItem
                                onClick={() =>
                                    handleColumnFilter("attached_doc")
                                }
                                className={cn("text-xs font-medium", {
                                    "text-primary": !attachDocFilter,
                                })}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {attachDocInClaims &&
                                attachDocInClaims.map((item) => (
                                    <DropdownItem
                                        key={item}
                                        onClick={() =>
                                            handleColumnFilter(
                                                "attached_doc",
                                                item === "Yes"
                                                    ? "true"
                                                    : "false"
                                            )
                                        }
                                        className={cn("text-xs font-medium", {
                                            "text-primary":
                                                (item === "Yes" &&
                                                    attachDocFilter ===
                                                        "true") ||
                                                (item === "No" &&
                                                    attachDocFilter ===
                                                        "false"),
                                        })}
                                    >
                                        {item}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },

        {
            accessorKey: "claim_id",
            header: "",
            cell: ({ row }) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    row.original.claim_activities,
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );
                return (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateClaimsActivity(
                                        isClaimAssignedToMe
                                            ? ClaimActivityType.UNLOCKED
                                            : ClaimActivityType.IN_PROGRESS,
                                        row.original.claim_id
                                    );
                                }}
                                disabled={updateClaimsActivity.isLoading}
                                className="normal-case"
                            >
                                {isClaimAssignedToMe
                                    ? "Unassign"
                                    : "Assign to me"}
                            </DropdownItem>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        row.original.claim_id,
                                        ClaimStatus.PENDING
                                    );
                                }}
                            >
                                Mark as Pending
                            </DropdownItem>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        row.original.claim_id,
                                        ClaimStatus.READY_FOR_SUBMISSION
                                    );
                                }}
                            >
                                Mark as Ready for submission
                            </DropdownItem>
                            {row.original.is_voided ? (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleChangeVoidStatus(
                                            false,
                                            row.original.claim_id
                                        );
                                    }}
                                >
                                    Unvoid Claim
                                </DropdownItem>
                            ) : (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleChangeVoidStatus(
                                            true,
                                            row.original.claim_id
                                        );
                                    }}
                                >
                                    Void Claim
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <>
            {statusToSubmit && selectedClaimsIds && (
                <Dialog open={dialogInView === "bulk_update_status"}>
                    <DialogContent
                        title="Update Claims status"
                        showFooter
                        saveText="Save changes"
                        cancelText="Cancel"
                        variant="center"
                        handleCloseDialog={handleCloseBulkUpdateStatusDialog}
                        submitBtnFormValue="bulk-update-claims"
                        isSubmitBtnDisabled={isMutating > 0}
                        isCancelBtnDisabled={isMutating > 0}
                    >
                        <BulkUpdateClaimForm
                            claimStatus={statusToSubmit}
                            claimsId={selectedClaimsIds}
                            onFormSubmit={handleCloseBulkUpdateStatusDialog}
                        />
                    </DialogContent>
                </Dialog>
            )}

            <TableFiltersDisplay
                appliedFilters={appliedFilters}
                wrapperClass="mb-12"
            />

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg="Cannot display claims at this time please try again later"
                />
            )}
            {isLoading && (
                <Skeleton
                    containerTestId="closed-claims-loader"
                    type="table"
                    count={6}
                />
            )}
            {data && isSuccess && (
                <>
                    {filteredData.length === 0 && (
                        <ListState
                            stateHelperText="Closed claims will appear here once available "
                            emptyMessage="No claims yet"
                            isError={false}
                        />
                    )}

                    {filteredData.length > 0 && (
                        <>
                            {getTotalAmountOfTableRowsSelected(rowSelection)
                                .amount > 0 && (
                                <div className="flex items-center mb-6">
                                    <div className="font-medium text-base shrink-0">
                                        {
                                            getTotalAmountOfTableRowsSelected(
                                                rowSelection
                                            ).amountString
                                        }
                                        :
                                    </div>
                                    <div className="flex items-center flex-wrap gap-12">
                                        {showAssignClaimsButton ? (
                                            <Button
                                                variant="secondary"
                                                onClick={() =>
                                                    handleUpdateClaimsActivity(
                                                        ClaimActivityType.IN_PROGRESS
                                                    )
                                                }
                                                disabled={
                                                    updateClaimsActivity.isLoading
                                                }
                                            >
                                                Assign claims to me
                                            </Button>
                                        ) : null}
                                        {showUnassignClaimsButton ? (
                                            <Button
                                                variant="secondary"
                                                onClick={() =>
                                                    handleUpdateClaimsActivity(
                                                        ClaimActivityType.UNLOCKED
                                                    )
                                                }
                                                disabled={
                                                    updateClaimsActivity.isLoading
                                                }
                                            >
                                                Unassign claims
                                            </Button>
                                        ) : null}
                                        <Button
                                            variant="secondary"
                                            className="ml-12"
                                            onClick={() =>
                                                handleDisplayBulkUpdateStatusDialog(
                                                    ClaimStatus.READY_FOR_SUBMISSION
                                                )
                                            }
                                        >
                                            Mark as Ready for submission
                                        </Button>
                                        {showUnvoidClaimsButton && (
                                            <Button
                                                variant="secondary"
                                                className="ml-12"
                                                onClick={() =>
                                                    handleChangeVoidStatus(
                                                        false
                                                    )
                                                }
                                            >
                                                Unvoid Claims
                                            </Button>
                                        )}

                                        {showVoidClaimsButton && (
                                            <Button
                                                variant="secondary"
                                                className="ml-12"
                                                onClick={() =>
                                                    handleChangeVoidStatus(true)
                                                }
                                            >
                                                Void Claims
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <Table
                                columns={columns}
                                data={filteredData}
                                setRowSelection={setRowSelection}
                                rowSelection={rowSelection}
                                setGetSelectedData={setGetSelectedClaimsData}
                                handleRowClick={handleRowClick}
                                hasPagination={data.total_count > 20}
                                isHeaderFixed
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
}
